import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import Tippy from '@tippyjs/react';

import { cn } from 'lib/helpers/utility';

import { showAlert } from 'components/shared/Alert/Alert';
import Checkbox from 'components/shared/Checkbox/Checkbox';
import RadioButton from 'components/shared/RadioButton/RadioButton';
import NewInfiniteScroll from 'components/shared/InfiniteScroll/NewInfiniteScroll';

import { fetchFilterOptions } from '../lib/fetchFilterOptions';

const DropdownItem = ({ item, index, selectedItems, values, handleSelectItem, singleSelect }) => {
  const isSelected = selectedItems.find((_item) => _item.value === item.value);
  const isChecked = values.find((_filter) => _filter.value === item.value);
  const selectedState = isChecked ? 'checked' : 'selected';
  const checked = !!isChecked || !!isSelected;
  const labelRef = useRef(null);

  const isTextTruncated = () => {
    if (!labelRef.current) return false;
    return labelRef.current.scrollWidth > labelRef.current.clientWidth;
  };

  const displayLabel = item?.secondaryLabel ? (
    <div className="flex w-full items-center justify-between gap-4">
      <span>{item?.label}</span>
      <span className="text-neutral-400">{item?.secondaryLabel}</span>
    </div>
  ) : (
    item?.label
  );

  return (
    <Tippy
      content={item?.label}
      disabled={!item?.label || !!item?.secondaryLabel || !isTextTruncated()}
      className="tippy-dark"
      placement="bottom">
      <div className="w-full">
        {singleSelect ? (
          <RadioButton
            label={displayLabel}
            value={item?.value}
            isSelected={checked}
            onChange={() => handleSelectItem(item, selectedState, checked)}
            wrapperClassName="h-8 !w-full cursor-pointer gap-2 px-4 hover:bg-neutral-50"
            className="overflow-hidden text-ellipsis whitespace-nowrap"
          />
        ) : (
          <Checkbox
            ref={labelRef}
            key={`${item?.label}-${index}`}
            size={16}
            label={displayLabel}
            className="h-8 !w-full cursor-pointer gap-2 p-4 hover:bg-neutral-50"
            labelTextColor="text-ellipsis w-full whitespace-nowrap overflow-hidden"
            innerLabelClassName="w-full"
            parentLabelClassName="w-full"
            labelClassName="w-full"
            onClick={() => handleSelectItem(item, selectedState, checked)}
            checked={checked}
          />
        )}
      </div>
    </Tippy>
  );
};

const DropdownList = ({
  items,
  data,
  dataKey,
  formula,
  onChange = () => {},
  dropdownClassName,
  setOpen
}) => {
  const [selectedItems, setSelectedItems] = useState(items || []);
  const [values, setValues] = useState(items || []);
  const dropdownRef = useRef(null);
  const [dims, setDims] = useState({ minHeight: 0, minWidth: 0 });

  const handleUpdateValues = (item) => {
    const isSelected = values.find((_item) => _item.value === item.value);
    if (isSelected) {
      setValues((prev) => prev.filter((_item) => _item.value !== item.value));
      setSelectedItems((prev) => prev.filter((_item) => _item.value !== item.value));
      return;
    }

    setValues((prev) => [...prev, item]);
    setSelectedItems((prev) => [...prev, item]);
  };

  const handleSelectItem = (item, state, checked) => {
    if (!state) return;

    if (data?.singleSelect) {
      if (checked) return;
      setSelectedItems([item]);
      setValues([item]);
      onChange([item], dataKey, formula);
      setOpen?.(false);
      return;
    }

    if (state === 'checked') {
      if (data?.default && selectedItems.length <= 1) {
        showAlert({
          title: 'This filter requires at least one selection.',
          color: 'warning'
        });
        return;
      }

      handleUpdateValues(item);
      const newValues = selectedItems.filter((_item) => _item.value !== item.value);
      onChange(newValues, dataKey, formula);
      return;
    }

    const isSelected = selectedItems.find((_item) => _item.value === item.value);
    if (isSelected) {
      setSelectedItems((prev) => prev.filter((_item) => _item.value !== item.value));
      const newValues = selectedItems.filter((_item) => _item.value !== item.value);
      onChange(newValues, dataKey, formula, checked);
      return;
    }

    setSelectedItems((prev) => [...prev, item]);
    const newValues = [...selectedItems, item];
    onChange(newValues, dataKey, formula);
  };

  const queryFn = useCallback(
    (navigate, pageParams) => fetchFilterOptions(navigate, data?.queryKey, pageParams),
    [data?.queryKey]
  );

  useLayoutEffect(() => {
    const el = dropdownRef.current;
    if (!el) return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      setDims({ minHeight: rect.height, minWidth: rect.width });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={dropdownRef}
      className={cn(
        'z-10 -my-2 flex max-h-[500px] w-fit min-w-[224px] max-w-[300px] overflow-hidden rounded-[4px] bg-white',
        dropdownClassName
      )}>
      <NewInfiniteScroll
        queryKey={data?.queryKey}
        queryFn={data?.queryFn || queryFn}
        formatData={data?.formatData}
        queryProps={data?.params}
        dependencies={data?.dependencies}
        values={values}
        minHeight={dims.minHeight}
        minWidth={dims.minWidth}
        isOptions={!data?.formatData}
        withSearch>
        {({ item, index }) => (
          <DropdownItem
            item={item}
            index={index}
            selectedItems={selectedItems}
            values={values}
            handleSelectItem={handleSelectItem}
            singleSelect={data?.singleSelect}
          />
        )}
      </NewInfiniteScroll>
    </div>
  );
};

export default DropdownList;
